import React, { forwardRef } from 'react';
import { clsx, createUseStyles, multiply } from '@pushpay/styles';
import { getButtonColor } from './button-color';
/**
 * Ratio of default large button height to default font size: 40 / 16
 */

export const LARGE_BUTTON_HEIGHT_RATIO = 2.5;
/**
 * Ratio of default large button padding to default font size: 24 / 16
 */

export const LARGE_BUTTON_PADDING_RATIO = 1.5;
/**
 * Ratio of default large button space between icons and text to default font size: 8 / 16
 */

export const LARGE_BUTTON_SPACING_RATIO = 0.5;
/**
 * Ratio of default small button height to default font size: 30 / 16
 */

export const SMALL_BUTTON_HEIGHT_RATIO = 1.875;
/**
 * Ratio of default small button padding to default font size: 12 / 16
 */

export const SMALL_BUTTON_PADDING_RATIO = 0.75;
/**
 * Ratio of default small button space between icons and text to default font size: 6 / 16
 */

export const SMALL_BUTTON_SPACING_RATIO = 0.375;
export const BUTTON_BORDER_WIDTH = '1px';
export const createButtonColorStyles = colorScheme => ({
  background: colorScheme.BACKGROUND,
  color: colorScheme.COLOR || undefined,
  borderColor: colorScheme.BORDER || undefined
});
export const createButtonStateStyles = colorScheme => ({ ...createButtonColorStyles(colorScheme.DEFAULT),
  '&:hover, &$__hover, &:focus': { ...createButtonColorStyles(colorScheme.HOVER)
  },
  '&:active, &$__active': { ...createButtonColorStyles(colorScheme.ACTIVE)
  },
  '&:disabled': {
    '&, &:hover, &:focus': { ...createButtonColorStyles(colorScheme.DISABLED)
    }
  }
});
export const useButtonStyles = createUseStyles(theme => {
  const buttonColor = getButtonColor(theme);
  return {
    root: {
      font: theme.TYPOGRAPHY.BUTTONS,
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      outline: 'none',
      border: 'none',
      backgroundClip: 'padding-box',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      backgroundColor: theme.colors['palette-transparent'],
      '&:not(:disabled)': {
        cursor: 'pointer'
      }
    },

    /**
     * Should only be used for testing
     * @deprecated should only be used for testing
     */
    __hover: {},

    /**
     * Should only be used for testing
     * @deprecated should only be used for testing
     */
    __active: {},
    large: {
      height: multiply('1em', LARGE_BUTTON_HEIGHT_RATIO),
      padding: `0 ${multiply('1em', LARGE_BUTTON_PADDING_RATIO)}`,
      borderRadius: multiply('1em', LARGE_BUTTON_HEIGHT_RATIO / 2),
      '& :not(:last-child)': {
        marginRight: multiply('1em', LARGE_BUTTON_SPACING_RATIO)
      },
      '& svg': {
        width: '1.25em',
        height: '1.25em'
      }
    },
    small: {
      height: multiply('1em', SMALL_BUTTON_HEIGHT_RATIO),
      padding: `0 ${multiply('1em', SMALL_BUTTON_PADDING_RATIO)}`,
      borderRadius: multiply('1em', SMALL_BUTTON_HEIGHT_RATIO / 2),
      '& :not(:last-child)': {
        marginRight: multiply('1em', SMALL_BUTTON_SPACING_RATIO)
      },
      '& svg': {
        width: '1em',
        height: '1em'
      }
    },
    unstyled: {
      padding: 0,
      font: 'inherit',
      color: 'inherit'
    },
    primary: { ...createButtonStateStyles(buttonColor.PRIMARY)
    },
    secondary: { ...createButtonStateStyles(buttonColor.SECONDARY),
      border: `${BUTTON_BORDER_WIDTH} solid currentcolor`,
      backgroundClip: 'border-box',
      '&$large': {
        height: `calc(${multiply('1em', LARGE_BUTTON_HEIGHT_RATIO)} - ${multiply(BUTTON_BORDER_WIDTH, 2)})`,
        padding: `0 calc(${multiply('1em', LARGE_BUTTON_PADDING_RATIO)} - ${BUTTON_BORDER_WIDTH})`
      },
      '&$small': {
        height: `calc(${multiply('1em', SMALL_BUTTON_HEIGHT_RATIO)} - ${multiply(BUTTON_BORDER_WIDTH, 2)})`,
        padding: `0 calc(${multiply('1em', SMALL_BUTTON_PADDING_RATIO)} - ${BUTTON_BORDER_WIDTH})`
      }
    },
    tertiary: { ...createButtonStateStyles(buttonColor.TERTIARY)
    },
    text: { ...createButtonStateStyles(buttonColor.TEXT)
    }
  };
});
export const Button = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    classes: classesProp,
    className: classNameProp,
    displayStyle = 'unstyled',
    displaySize,
    children,
    type,
    onClick,
    ...rest
  } = props;
  const classes = useButtonStyles(classesProp);
  let computedDisplaySize;

  if (displayStyle === 'unstyled') {
    computedDisplaySize = null;
  } else if (!displaySize) {
    computedDisplaySize = displayStyle === 'text' ? 'small' : 'large';
  } else {
    computedDisplaySize = displaySize;
  }

  const className = clsx(classes.root, classes[displayStyle], computedDisplaySize && classes[computedDisplaySize], classNameProp);
  return (
    /*#__PURE__*/
    // Lint complains "`type` is an invalid value for button type attribute"
    // disable the rule as work-around (https://github.com/yannickcr/eslint-plugin-react/issues/1555)
    // eslint-disable-next-line react/button-has-type
    React.createElement("button", Object.assign({
      className: className,
      onClick: onClick,
      type: type,
      ref: ref
    }, rest), children)
  );
});